import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, AlertTitle, CircularProgress, Chip, Stack, Paper,
  Grid, Divider, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';

// RCCS 준수 활동 이력 — "언제 · 어느 항목/조항에 · 무엇을 했고 · 그래서 어떻게 됐는가".
// 데이터: GET /api/compliance/activity
//
// 🔴 이 화면의 존재 이유는 준수 현황(/compliance)이 답하지 못하는 질문이다.
//   현황은 **지금 상태**만 보여준다. "왜 이렇게 됐는지", "나아지고 있는지",
//   "언제 인지하고 무엇을 했는지"는 이력이 없으면 git 을 뒤져야 알 수 있었다.
//   심사원이 묻는 것도 상태가 아니라 활동의 이력이다.
//
// 🔴 소급 정리분(backfill)과 당시 기록(live)을 구분해서 보여준다.
//   사후에 정리한 것을 당시 기록처럼 보이게 하면 그 순간 증적이 아니게 된다.
//
// 🔴 전수가 아님을 화면에서 밝힌다.
//   대장은 2026-08-11 이후만 소급했고 근거 없는 항목은 만들지 않았다.
//   "이게 전부"로 읽히면 거짓이 된다.

const KIND_COLOR = {
  define: 'primary',      // 기준 수립
  measure: 'info',        // 실측
  remediate: 'success',   // 조치
  decide: 'secondary',    // 결정
  discover: 'warning',    // 발견
  document: 'default',    // 문서화
  fix: 'error',           // 수정
};

function Entry({ e, isLast }) {
  const theme = useTheme();
  const color = KIND_COLOR[e.kind] || 'default';
  const dot = theme.palette[color]?.main || theme.palette.grey[500];

  return (
    <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
      {/* 타임라인 축 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2.4 }}>
        <Box sx={{
          width: 11, height: 11, borderRadius: '50%', bgcolor: dot, flexShrink: 0,
          outline: `3px solid ${theme.palette.background.paper}`,
        }} />
        {!isLast && (
          <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', mt: 0.5, minHeight: 24 }} />
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 1.5, flex: 1, minWidth: 0 }}>
        <Stack direction="row" flexWrap="wrap" alignItems="baseline" gap={1} sx={{ mb: 0.8 }}>
          <Typography sx={{
            fontSize: 12.5, fontFamily: 'monospace', fontWeight: 700, color: 'text.secondary',
          }}>
            {e.date}
          </Typography>
          <Chip size="small" color={color} variant="outlined" label={e.kind_label}
            sx={{ height: 19, fontSize: 10.5, fontWeight: 700 }} />
          {e.source === 'backfill' && (
            <Tooltip title="2026-09-03 에 소급 정리한 항목입니다 — 당시 기록이 아닙니다">
              <Chip size="small" label="소급" variant="outlined"
                sx={{ height: 19, fontSize: 10, borderStyle: 'dashed', color: 'text.disabled' }} />
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.disabled' }}>
            {e.id}
          </Typography>
        </Stack>

        <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', mb: 0.8 }}>
          {e.title}
        </Typography>

        {/* 🔴 가장 중요한 줄 — 그래서 어떻게 됐는가 */}
        {e.outcome && (
          <Typography sx={{
            fontSize: 13.5, lineHeight: 1.7, color: 'text.secondary',
            pl: 1.3, borderLeft: 2, borderColor: dot, mb: 1,
          }}>
            {e.outcome}
          </Typography>
        )}

        <Stack direction="row" flexWrap="wrap" gap={0.6} alignItems="center">
          {e.items.map((it) => (
            <Tooltip key={it} title="점검 항목">
              <Chip size="small" label={it} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
            </Tooltip>
          ))}
          {e.clauses.map((cl) => (
            <Tooltip key={cl} title="조항">
              <Chip size="small" variant="outlined" label={cl}
                sx={{ height: 20, fontSize: 11, fontFamily: 'monospace' }} />
            </Tooltip>
          ))}
        </Stack>

        {e.evidence?.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={{ xs: 0.4, sm: 1.2 }} sx={{ mt: 1 }}>
            {e.evidence.map((ev) => (
              <Typography key={ev} sx={{
                fontSize: 11.5, fontFamily: 'monospace', color: 'text.disabled',
              }}>
                {ev}
              </Typography>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
}

export default function RccsActivityPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');

  useEffect(() => {
    let alive = true;
    fetch('/api/compliance/activity')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { hint: j.hint });
        return j;
      })
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const shown = useMemo(() => {
    const all = data?.entries || [];
    return kind === 'all' ? all : all.filter((e) => e.kind === kind);
  }, [data, kind]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (err) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        <AlertTitle>{err.message}</AlertTitle>
        {err.hint}
      </Alert>
    );
  }

  const t = data.totals || {};
  const labels = data.kind_labels || {};

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <HistoryIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          준수 활동 이력
        </Typography>
      </Stack>
      <Typography sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 720 }}>
        언제, 어느 항목·조항에, 무엇을 했고, 그래서 어떻게 됐는가. 준수 현황이
        <b> 지금 상태</b>를 보여준다면 이 화면은 <b>거기까지 온 과정</b>을 보여줍니다.
      </Typography>

      <Grid container spacing={1} sx={{ mb: 2.5 }}>
        {[
          ['전체 활동', t.entries],
          ['당시 기록', t.live],
          ['소급 정리', t.backfill],
          ['연결 항목', t.items_touched],
          ['연결 조항', t.clauses_touched],
        ].map(([label, val]) => (
          <Grid item xs={6} sm={2.4} key={label}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography sx={{
                fontSize: 26, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.1,
              }}>
                {val ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* 🔴 전수가 아님을 밝힌다 */}
      {data.note && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2.5 }}>
          <AlertTitle sx={{ fontWeight: 750 }}>이 이력은 전수가 아닙니다</AlertTitle>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.7 }}>{data.note}</Typography>
          <Typography sx={{ fontSize: 13.5, lineHeight: 1.7, mt: 0.8 }}>
            <b>소급</b> 표시가 붙은 항목은 {data.backfilled_at} 에 정리한 것이며 당시 기록이
            아닙니다. 근거가 확인되지 않는 활동은 만들지 않았으므로, 빠진 것은 git 이력과
            <code> decisions/</code> 를 정본으로 봅니다.
          </Typography>
        </Alert>
      )}

      <ToggleButtonGroup size="small" value={kind} exclusive sx={{ mb: 2, flexWrap: 'wrap' }}
        onChange={(_, v) => v && setKind(v)}>
        <ToggleButton value="all" sx={{ fontSize: 12, py: 0.4 }}>
          전체 {t.entries ?? 0}
        </ToggleButton>
        {Object.entries(t.by_kind || {})
          .sort((a, b) => b[1] - a[1])
          .map(([k, n]) => (
            <ToggleButton key={k} value={k} sx={{ fontSize: 12, py: 0.4 }}>
              {labels[k] || k} {n}
            </ToggleButton>
          ))}
      </ToggleButtonGroup>

      <Box>
        {shown.map((e, i) => (
          <Entry key={e.id} e={e} isLast={i === shown.length - 1} />
        ))}
        {shown.length === 0 && (
          <Typography sx={{ color: 'text.disabled', py: 4, textAlign: 'center' }}>
            해당 종류의 활동이 없습니다.
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.8 }}>
        대장은 <b>append-only</b> 입니다 — 기존 항목을 고치지 않고, 정정은 새 항목으로
        이전 항목을 가리킵니다. 사후 수정된 이력은 증적으로 인정되지 않기 때문입니다.<br />
        등재 누락은 생성기가 <b>git 이력과 대조</b>해 경고합니다 — 준수 경로를 건드린 커밋에
        대응 항목이 없으면 주간 검사에서 드러납니다.<br />
        대장 <code>operations/registers/rccs-activity-log.yaml</code> · 생성기{' '}
        <code>scripts/compliance-check/build_activity_feed.py</code>
      </Typography>
    </Box>
  );
}
